import { useState } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { Calendar } from "./ui/calendar.tsx";
import { toast } from "../hooks/use-toast";
import { CalendarCheck, Clock, User, Check } from "lucide-react";

const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
];

const services = [
  { 
    id: "corte", 
    name: "Corte de Cabelo", 
    description: "Corte moderno e personalizado para seu estilo",
    price: "R$ 50,00",
    image: "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=400"
  },
  { 
    id: "coloracao", 
    name: "Coloração", 
    description: "Coloração profissional com produtos de alta qualidade",
    price: "R$ 150,00",
    image: "https://images.unsplash.com/photo-1595476108010-b4d1f102b1b1?w=400"
  },
  { 
    id: "tratamento", 
    name: "Tratamento Capilar", 
    description: "Hidratação e reconstrução para cabelos danificados",
    price: "R$ 120,00",
    image: "https://images.unsplash.com/photo-1559599101-f09722fb4948?w=400"
  },
  { 
    id: "manicure", 
    name: "Manicure & Pedicure", 
    description: "Cuidados completos para suas unhas",
    price: "R$ 60,00",
    image: "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=400"
  },
  { 
    id: "maquiagem", 
    name: "Maquiagem", 
    description: "Maquiagem profissional para eventos especiais",
    price: "R$ 180,00",
    image: "https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?w=400"
  },
  { 
    id: "barba", 
    name: "Barba", 
    description: "Modelagem e tratamento completo de barba",
    price: "R$ 35,00",
    image: "https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=400"
  },
];

const professionals = [
  { 
    id: "ana", 
    name: "Ana Silva", 
    role: "Cabeleireira",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200"
  },
  { 
    id: "carlos", 
    name: "Carlos Santos", 
    role: "Barbeiro",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200"
  },
  { 
    id: "maria", 
    name: "Maria Oliveira", 
    role: "Manicure",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200"
  },
  { 
    id: "julia", 
    name: "Júlia Costa", 
    role: "Maquiadora",
    image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200"
  },
];

const BookingSection = () => {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [service, setService] = useState("");
  const [professional, setProfessional] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !time || !service || !professional || !name || !phone) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));

    const selectedProfessional = professionals.find(p => p.id === professional);
    
    toast({
      title: "Agendamento confirmado! ✓",
      description: `${name}, seu horário com ${selectedProfessional?.name} foi reservado para ${date.toLocaleDateString('pt-BR')} às ${time}.`,
    });

    setDate(undefined);
    setTime("");
    setService("");
    setProfessional("");
    setName("");
    setPhone("");
    setIsSubmitting(false);
  };

  return (
    <section id="agendamento" className="py-5">
      <Container className="py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <span className="d-inline-block text-primary-custom fw-semibold mb-3">
            Agendamento Online
          </span>
          <h2 className="display-6 fw-bold mb-3">
            Reserve seu horário agora
          </h2>
          <p className="text-muted-custom mx-auto" style={{ maxWidth: "600px" }}>
            Selecione o serviço, profissional, data e horário de sua preferência. 
            É rápido, fácil e você recebe a confirmação na hora.
          </p>
        </div>

        {/* Service Selection - Cards */}
        <div className="mb-5">
          <h5 className="fw-semibold mb-4 d-flex align-items-center gap-2">
            <span className="step-number">1</span>
            Escolha o serviço
          </h5>
          <Row className="g-4">
            {services.map((s) => (
              <Col key={s.id} sm={6} md={4} lg={3}>
                <Card 
                  className={`h-100 border-0 shadow-card service-card ${service === s.id ? 'selected' : ''}`}
                  style={{ cursor: "pointer", borderRadius: "1rem", overflow: "hidden" }}
                  onClick={() => setService(s.id)}
                >
                  <div className="position-relative">
                    <Card.Img 
                      variant="top" 
                      src={s.image} 
                      alt={s.name}
                      style={{ height: "140px", objectFit: "cover" }}
                    />
                    {service === s.id && (
                      <div className="selected-badge position-absolute top-0 end-0 m-2">
                        <span className="badge gradient-primary rounded-circle p-2">
                          <Check size={14} />
                        </span>
                      </div>
                    )}
                  </div>
                  <Card.Body className="p-3">
                    <Card.Title className="fs-6 fw-bold mb-1">{s.name}</Card.Title>
                    <Card.Text className="small text-muted-custom mb-2" style={{ minHeight: "40px" }}>
                      {s.description}
                    </Card.Text>
                    <span className="fw-bold text-primary-custom">{s.price}</span>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Professional Selection */}
        <div className="mb-5">
          <h5 className="fw-semibold mb-4 d-flex align-items-center gap-2">
            <span className="step-number">2</span>
            Escolha o profissional
          </h5>
          <Row className="g-3">
            {professionals.map((p) => (
              <Col key={p.id} xs={6} sm={4} md={3} lg={2}>
                <div 
                  className={`professional-card text-center p-3 rounded-4 ${professional === p.id ? 'selected' : ''}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setProfessional(p.id)}
                >
                  <div className="position-relative d-inline-block mb-2">
                    <img 
                      src={p.image} 
                      alt={p.name}
                      className="rounded-circle"
                      style={{ width: "70px", height: "70px", objectFit: "cover" }}
                    />
                    {professional === p.id && (
                      <span className="position-absolute bottom-0 end-0 badge gradient-primary rounded-circle p-1">
                        <Check size={12} />
                      </span>
                    )}
                  </div>
                  <h6 className="mb-0 small fw-semibold">{p.name}</h6>
                  <small className="text-muted-custom">{p.role}</small>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Date, Time and Personal Info */}
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="border-0 shadow-card" style={{ borderRadius: "1.5rem" }}>
              <Card.Body className="p-4 p-md-5">
                <Form onSubmit={handleSubmit}>
                  <Row className="g-4">
                    {/* Left Column - Calendar */}
                    <Col lg={6}>
                      <h5 className="fw-semibold mb-4 d-flex align-items-center gap-2">
                        <span className="step-number">3</span>
                        Data e horário
                      </h5>
                      <Form.Label className="fw-semibold d-flex align-items-center gap-2 mb-3">
                        <CalendarCheck size={20} className="text-primary-custom" />
                        Escolha a data
                      </Form.Label>
                      <div className="d-flex justify-content-center bg-muted rounded-4 p-3">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date() || date.getDay() === 0}
                          className="pointer-events-auto"
                        />
                      </div>
                    </Col>

                    {/* Right Column - Form Fields */}
                    <Col lg={6}>
                      {/* Time Select */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                          <Clock size={20} className="text-primary-custom" />
                          Horário
                        </Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                          {timeSlots.map((slot) => (
                            <button
                              key={slot}
                              type="button"
                              onClick={() => setTime(slot)}
                              className={`time-slot ${time === slot ? 'active' : ''}`}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      </Form.Group>

                      {/* Personal Info */}
                      <h5 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                        <span className="step-number">4</span>
                        Seus dados
                      </h5>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold d-flex align-items-center gap-2">
                          <User size={20} className="text-primary-custom" />
                          Informações de contato
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Seu nome completo"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="form-control-custom mb-3"
                        />
                        <Form.Control
                          type="tel"
                          placeholder="Seu telefone (WhatsApp)"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="form-control-custom"
                        />
                      </Form.Group>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="btn-primary-custom w-100 py-3"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            <span className="spinner-border spinner-border-sm" />
                            Confirmando...
                          </span>
                        ) : (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            <CalendarCheck size={20} />
                            Confirmar Agendamento
                          </span>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default BookingSection;